package dev.danko.roblox_craft_ai.pages

import dev.danko.roblox_craft_ai.components.SiteSection
import dev.danko.roblox_craft_ai.components.SiteHeader
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.client.AdminApiClient
import dev.danko.roblox_craft_ai.client.ApiException
import dev.danko.roblox_craft_ai.components.layouts.PageLayoutData
import dev.danko.roblox_craft_ai.shared.model.AccessRequestAdminDto
import dev.danko.roblox_craft_ai.shared.model.AdminAuditDto
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.shared.model.AdminUsageReport
import dev.danko.roblox_craft_ai.shared.model.AdminUserDto
import dev.danko.roblox_craft_ai.shared.model.BillingPlanDto
import dev.danko.roblox_craft_ai.shared.model.CreditPackDto
import dev.danko.roblox_craft_ai.shared.model.ModerationIncidentDto
import dev.danko.roblox_craft_ai.state.AuthState
import dev.danko.roblox_craft_ai.state.Txt
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.AppCard
import dev.danko.roblox_craft_ai.uikit.components.AppInput
import dev.danko.roblox_craft_ai.uikit.components.ModalDialogConfirmActions
import dev.danko.roblox_craft_ai.uikit.components.ModalDialog
import dev.danko.roblox_craft_ai.uikit.components.AppSwitch
import dev.danko.roblox_craft_ai.uikit.components.AppTabs
import dev.danko.roblox_craft_ai.uikit.components.TabItem
import dev.danko.roblox_craft_ai.uikit.components.LanguageSwitcher
import dev.danko.roblox_craft_ai.uikit.components.ToastManager
import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

private enum class AdminTab { OVERVIEW, REQUESTS, PLANS, PACKS, USERS, MODERATION, AUDIT }

@InitRoute
@Suppress("unused")
fun initAdminPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(Txt.admin.pageTitle))
}

private fun dateTime(ms: Long?): String = ms?.let { kotlin.js.Date(it.toDouble()).toLocaleString(LocaleController.current.code) } ?: "—"

private fun usd(value: Double): String = "$" + (kotlin.math.round(value * 100) / 100).toString()

/** Runs a save action and reports the outcome with a toast; validation messages from the server are shown as is. */
private fun CoroutineScope.saving(onDone: suspend () -> Unit = {}, action: suspend () -> Unit) = launch {
    try {
        action()
        ToastManager.success(Txt.admin.saved)
        onDone()
    } catch (e: ApiException) {
        ToastManager.error(Txt.admin.saveFailed(e.serverMessage))
    } catch (t: Throwable) {
        ToastManager.error(Txt.admin.saveFailed(t.message ?: ""))
    }
}

@Composable
@Page("/admin")
fun AdminPage() {
    val s = strings().admin
    val palette = LocalAppTheme.current.colors
    val ctx = rememberPageContext()
    var allowed by remember { mutableStateOf<Boolean?>(null) }
    var tab by remember { mutableStateOf(AdminTab.OVERVIEW) }
    var pendingRequests by remember { mutableStateOf(0) }

    LaunchedEffect(AuthState.user?.id) {
        allowed = AdminApiClient.isAdmin()
        if (allowed == false) ctx.router.navigateTo("/")
        if (allowed == true) {
            pendingRequests = runCatching { AdminApiClient.accessRequests("pending").size }.getOrDefault(0)
            if (pendingRequests > 0) tab = AdminTab.REQUESTS
        }
    }

    SiteHeader(SiteSection.ADMIN)
    Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
        Column(Modifier.fillMaxWidth().maxWidth(80.cssRem).padding(1.cssRem).gap(1.cssRem)) {

            SpanText(s.title, Modifier.fontSize(1.8.cssRem).fontWeight(FontWeight.Bold).color(palette.textPrimary))
            if (allowed != true) return@Column

            AppTabs(
                tabs = listOf(
                    TabItem(AdminTab.OVERVIEW, s.tabOverview),
                    TabItem(AdminTab.REQUESTS, "✉️ " + strings().access.adminTab, count = pendingRequests.takeIf { it > 0 }),
                    TabItem(AdminTab.PLANS, s.tabPlans),
                    TabItem(AdminTab.PACKS, s.tabPacks),
                    TabItem(AdminTab.USERS, s.tabUsers),
                    TabItem(AdminTab.MODERATION, s.tabModeration),
                    TabItem(AdminTab.AUDIT, s.tabAudit)
                ),
                activeTab = tab,
                onTabSelected = { tab = it }
            )

            when (tab) {
                AdminTab.OVERVIEW -> OverviewTab()
                AdminTab.REQUESTS -> RequestsTab(onPendingChanged = { pendingRequests = it })
                AdminTab.PLANS -> PlansTab()
                AdminTab.PACKS -> PacksTab()
                AdminTab.USERS -> UsersTab()
                AdminTab.MODERATION -> ModerationTab()
                AdminTab.AUDIT -> AuditTab()
            }
        }
    }
}

@Composable
private fun Muted(text: String) {
    SpanText(text, Modifier.fontSize(0.85.cssRem).color(LocalAppTheme.current.colors.textSecondary))
}

@Composable
private fun Strong(text: String) {
    SpanText(text, Modifier.fontWeight(FontWeight.SemiBold).color(LocalAppTheme.current.colors.textPrimary))
}

/** "4.99 USD" for 499 cents: a preview next to price fields that are entered in cents. */
private fun money(cents: Int?, currency: String): String =
    "= " + ((cents ?: 0) / 100.0).asDynamic().toFixed(2) as String + " " + currency

/**
 * Read-only table: muted header, aligned numeric columns ([numeric] = column indexes), row dividers.
 * Scrolls sideways on narrow screens instead of squeezing the page.
 */
@Composable
private fun DataTable(headers: List<String>, rows: List<List<String>>, numeric: Set<Int> = emptySet()) {
    val palette = LocalAppTheme.current.colors
    Div(Modifier.fillMaxWidth().overflow { x(Overflow.Auto) }.toAttrs()) {
        Table(Modifier.fillMaxWidth().styleModifier {
            property("border-collapse", "collapse")
            property("font-size", "0.9rem")
            property("font-variant-numeric", "tabular-nums")
        }.toAttrs()) {
            Thead {
                Tr {
                    headers.forEachIndexed { i, header ->
                        Th(Modifier.padding(topBottom = 0.5.cssRem, leftRight = 0.6.cssRem).color(palette.textSecondary).styleModifier {
                            property("text-align", if (i in numeric) "right" else "left")
                            property("font-weight", "600")
                            property("font-size", "0.78rem")
                            property("white-space", "nowrap")
                            property("border-bottom", "1px solid ${palette.borderStrong}")
                        }.toAttrs()) { Text(header) }
                    }
                }
            }
            Tbody {
                rows.forEach { row ->
                    Tr {
                        row.forEachIndexed { i, cell ->
                            Td(Modifier.padding(topBottom = 0.5.cssRem, leftRight = 0.6.cssRem).color(palette.textPrimary).styleModifier {
                                property("text-align", if (i in numeric) "right" else "left")
                                property("border-bottom", "1px solid ${palette.border}")
                                property("vertical-align", "top")
                                if (i in numeric) property("white-space", "nowrap")
                            }.toAttrs()) { Text(cell) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun Field(label: String, value: String, onChange: (String) -> Unit, width: Double = 12.0, placeholder: String = "") {
    Box(Modifier.flexGrow(1).flexBasis(width.cssRem).minWidth(8.cssRem)) {
        AppInput(value = value, onValueChanged = onChange, label = label, placeholder = placeholder, modifier = Modifier.fillMaxWidth())
    }
}

@Composable
private fun OverviewTab() {
    val s = strings().admin
    var days by remember { mutableStateOf(7) }
    var report by remember { mutableStateOf<AdminUsageReport?>(null) }
    LaunchedEffect(days) { report = runCatching { AdminApiClient.usage(days) }.getOrNull() }

    Row(Modifier.gap(0.4.cssRem)) {
        listOf(7 to s.days7, 30 to s.days30).forEach { (value, label) ->
            AppButton(text = label, onClick = { days = value }, variant = if (days == value) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE, size = AppButtonSize.SM)
        }
    }
    val r = report ?: return
    Row(Modifier.fillMaxWidth().gap(1.cssRem).flexWrap(FlexWrap.Wrap)) {
        listOf(
            s.totalCost to usd(r.totalCostUsd),
            s.anonymousCost to usd(r.anonymousCostUsd),
            s.totalEvents to r.days.sumOf { it.events }.toString()
        ).forEach { (label, value) ->
            AppCard(Modifier.flexGrow(1).flexBasis(12.cssRem)) {
                Column(Modifier.gap(0.25.cssRem)) {
                    Muted(label)
                    SpanText(value, Modifier.fontSize(1.6.cssRem).fontWeight(FontWeight.Bold).color(LocalAppTheme.current.colors.textPrimary))
                }
            }
        }
    }
    AppCard(Modifier.fillMaxWidth()) {
        Column(Modifier.gap(0.6.cssRem)) {
            Strong(s.byDay)
            if (r.days.isEmpty()) Muted(s.noData) else DataTable(
                headers = listOf(s.day, s.cost, s.events.replaceFirstChar { it.uppercase() }, s.providers),
                rows = r.days.map { day ->
                    listOf(day.day, usd(day.costUsd), day.events.toString(), day.byProvider.entries.joinToString(" · ") { "${it.key} ${usd(it.value)}" })
                },
                numeric = setOf(1, 2)
            )
        }
    }
    AppCard(Modifier.fillMaxWidth()) {
        Column(Modifier.gap(0.6.cssRem)) {
            Strong(s.topUsers)
            if (r.topUsers.isEmpty()) Muted(s.noData) else DataTable(
                headers = listOf(s.user, s.cost, s.events.replaceFirstChar { it.uppercase() }),
                rows = r.topUsers.map { listOf(it.userId, usd(it.costUsd), it.events.toString()) },
                numeric = setOf(1, 2)
            )
        }
    }
}

@Composable
private fun RequestsTab(onPendingChanged: (Int) -> Unit) {
    val s = strings().access
    val palette = LocalAppTheme.current.colors
    val scope = rememberCoroutineScope()
    var filter by remember { mutableStateOf("pending") }
    var requests by remember { mutableStateOf<List<AccessRequestAdminDto>>(emptyList()) }
    var version by remember { mutableStateOf(0) }
    LaunchedEffect(filter, version) {
        requests = runCatching { AdminApiClient.accessRequests(filter.takeIf { it != "all" }) }.getOrDefault(emptyList())
        if (filter == "pending") onPendingChanged(requests.size)
        else onPendingChanged(runCatching { AdminApiClient.accessRequests("pending").size }.getOrDefault(0))
    }

    Row(Modifier.gap(0.4.cssRem).flexWrap(FlexWrap.Wrap)) {
        listOf("pending" to s.pending, "approved" to s.approved, "rejected" to s.rejected, "all" to s.all).forEach { (value, label) ->
            AppButton(text = label, onClick = { filter = value }, variant = if (filter == value) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE, size = AppButtonSize.SM)
        }
    }
    if (requests.isEmpty()) Muted(s.noRequests)
    requests.forEach { request ->
        AppCard(Modifier.fillMaxWidth()) {
            Column(Modifier.gap(0.45.cssRem)) {
                Row(Modifier.fillMaxWidth().gap(0.6.cssRem).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                    Strong(request.email)
                    if (request.name.isNotBlank()) Muted(request.name)
                    val (label, color) = when (request.status) {
                        "APPROVED" -> s.approved to palette.statusReady
                        "REJECTED" -> s.rejected to palette.statusFailed
                        else -> s.pending to palette.statusPending
                    }
                    SpanText(
                        label,
                        Modifier.fontSize(0.75.cssRem).fontWeight(FontWeight.SemiBold).color(color)
                            .padding(topBottom = 0.15.cssRem, leftRight = 0.5.cssRem).borderRadius(999.px)
                            .backgroundColor(color.toRgb().copy(alpha = 40))
                    )
                }
                request.message?.let { SpanText("💬 $it", Modifier.fontSize(0.9.cssRem).color(LocalAppTheme.current.colors.textPrimary)) }
                Muted(dateTime(request.createdAtMs) + (request.decidedBy?.let { " · ${dateTime(request.decidedAtMs)} · $it" } ?: ""))
                if (request.status != "APPROVED") {
                    Row(Modifier.gap(0.5.cssRem)) {
                        AppButton(text = s.approve, icon = AppIcon.CHECK, size = AppButtonSize.SM, onClick = {
                            scope.launch {
                                runCatching { AdminApiClient.approveAccess(request.id) }
                                    .onSuccess { ToastManager.success(s.approvedToast); version++ }
                                    .onFailure { ToastManager.error(Txt.admin.saveFailed(it.message ?: "")) }
                            }
                        })
                        if (request.status == "PENDING") {
                            AppButton(text = s.reject, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM, onClick = {
                                scope.launch {
                                    runCatching { AdminApiClient.rejectAccess(request.id) }
                                        .onSuccess { ToastManager.info(s.rejectedToast); version++ }
                                        .onFailure { ToastManager.error(Txt.admin.saveFailed(it.message ?: "")) }
                                }
                            })
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun PlansTab() {
    val s = strings().admin
    var plans by remember { mutableStateOf<List<BillingPlanDto>>(emptyList()) }
    var version by remember { mutableStateOf(0) }
    LaunchedEffect(version) { plans = runCatching { AdminApiClient.plans() }.getOrDefault(emptyList()) }
    Muted("⚠️ ${s.priceWarning}")
    plans.forEach { plan -> PlanEditor(plan) { version++ } }
}

@Composable
private fun PlanEditor(plan: BillingPlanDto, onSaved: () -> Unit) {
    val s = strings().admin
    val scope = rememberCoroutineScope()
    var name by remember(plan) { mutableStateOf(plan.name) }
    var credits by remember(plan) { mutableStateOf(plan.monthlyCredits?.toString().orEmpty()) }
    var price by remember(plan) { mutableStateOf(plan.priceCents.toString()) }
    var currency by remember(plan) { mutableStateOf(plan.currency) }
    var priceId by remember(plan) { mutableStateOf(plan.paddlePriceId.orEmpty()) }
    var active by remember(plan) { mutableStateOf(plan.active) }
    AppCard(Modifier.fillMaxWidth()) {
        Column(Modifier.gap(0.6.cssRem)) {
            Strong("${plan.id}")
            Row(Modifier.fillMaxWidth().gap(0.6.cssRem).flexWrap(FlexWrap.Wrap)) {
                Field(s.name, name, { name = it })
                Field(s.monthlyCredits, credits, { credits = it.filter(Char::isDigit) }, placeholder = s.unlimitedHint)
                Field(s.price, price, { price = it.filter(Char::isDigit) }, width = 8.0)
                Field(s.currency, currency, { currency = it.take(3).uppercase() }, width = 6.0)
                Field(s.paddlePriceId, priceId, { priceId = it.trim() }, width = 16.0, placeholder = "pri_…")
            }
            Row(Modifier.gap(1.cssRem).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                Muted(money(price.toIntOrNull(), currency))
                AppSwitch(checked = active, onCheckedChange = { active = it }, label = s.active)
                Spacer()
                AppButton(text = s.save, size = AppButtonSize.SM, onClick = {
                    scope.saving(onDone = { onSaved() }) {
                        AdminApiClient.savePlan(
                            plan.copy(
                                name = name,
                                monthlyCredits = credits.toIntOrNull(),
                                priceCents = price.toIntOrNull() ?: 0,
                                currency = currency,
                                paddlePriceId = priceId.ifBlank { null },
                                active = active
                            )
                        )
                    }
                })
            }
        }
    }
}

@Composable
private fun PacksTab() {
    val s = strings().admin
    var packs by remember { mutableStateOf<List<CreditPackDto>>(emptyList()) }
    var version by remember { mutableStateOf(0) }
    LaunchedEffect(version) { packs = runCatching { AdminApiClient.packs() }.getOrDefault(emptyList()) }
    Muted("⚠️ ${s.priceWarning}")
    packs.forEach { pack -> PackEditor(pack, isNew = false) { version++ } }
    Strong(s.addPack)
    PackEditor(CreditPackDto(id = "", name = "", credits = 100, priceCents = 0), isNew = true) { version++ }
}

@Composable
private fun PackEditor(pack: CreditPackDto, isNew: Boolean, onSaved: () -> Unit) {
    val s = strings().admin
    val scope = rememberCoroutineScope()
    var id by remember(pack) { mutableStateOf(pack.id) }
    var name by remember(pack) { mutableStateOf(pack.name) }
    var credits by remember(pack) { mutableStateOf(pack.credits.toString()) }
    var price by remember(pack) { mutableStateOf(pack.priceCents.toString()) }
    var currency by remember(pack) { mutableStateOf(pack.currency) }
    var priceId by remember(pack) { mutableStateOf(pack.paddlePriceId.orEmpty()) }
    var active by remember(pack) { mutableStateOf(pack.active) }
    AppCard(Modifier.fillMaxWidth()) {
        Column(Modifier.gap(0.6.cssRem)) {
            Row(Modifier.fillMaxWidth().gap(0.6.cssRem).flexWrap(FlexWrap.Wrap)) {
                if (isNew) Field(s.packId, id, { id = it.lowercase().filter { c -> c.isLetterOrDigit() || c == '_' } }, width = 10.0) else Strong(pack.id)
                Field(s.name, name, { name = it })
                Field(s.credits, credits, { credits = it.filter(Char::isDigit) }, width = 8.0)
                Field(s.price, price, { price = it.filter(Char::isDigit) }, width = 8.0)
                Field(s.currency, currency, { currency = it.take(3).uppercase() }, width = 6.0)
                Field(s.paddlePriceId, priceId, { priceId = it.trim() }, width = 16.0, placeholder = "pri_…")
            }
            Row(Modifier.gap(1.cssRem).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                Muted(money(price.toIntOrNull(), currency))
                AppSwitch(checked = active, onCheckedChange = { active = it }, label = s.active)
                Spacer()
                AppButton(text = if (isNew) s.addPack else s.save, size = AppButtonSize.SM, onClick = {
                    scope.saving(onDone = { onSaved() }) {
                        val edited = pack.copy(
                            id = id,
                            name = name,
                            credits = credits.toIntOrNull() ?: 0,
                            priceCents = price.toIntOrNull() ?: 0,
                            currency = currency,
                            paddlePriceId = priceId.ifBlank { null },
                            active = active
                        )
                        if (isNew) AdminApiClient.createPack(edited) else AdminApiClient.savePack(edited)
                    }
                })
            }
        }
    }
}

@Composable
private fun UsersTab() {
    val s = strings().admin
    var query by remember { mutableStateOf("") }
    var users by remember { mutableStateOf<List<AdminUserDto>>(emptyList()) }
    var version by remember { mutableStateOf(0) }
    var error by remember { mutableStateOf<String?>(null) }
    var loaded by remember { mutableStateOf(false) }
    LaunchedEffect(query, version) {
        kotlinx.coroutines.delay(250)
        runCatching { AdminApiClient.users(query) }
            .onSuccess { users = it; error = null }
            .onFailure { error = it.message ?: it.toString() }
        loaded = true
    }
    AppInput(value = query, onValueChanged = { query = it }, placeholder = s.searchUsers, modifier = Modifier.fillMaxWidth())
    error?.let { Muted("⚠️ $it") }
    if (loaded && users.isEmpty() && error == null) Muted(s.noUsers)
    users.forEach { user -> UserCard(user) { version++ } }
}

@Composable
private fun UserCard(user: AdminUserDto, onChanged: () -> Unit) {
    val s = strings().admin
    val palette = LocalAppTheme.current.colors
    val scope = rememberCoroutineScope()
    var delta by remember(user.id) { mutableStateOf("") }
    var confirmDelete by remember(user.id) { mutableStateOf(false) }
    ModalDialog(
        visible = confirmDelete,
        onCloseRequest = { confirmDelete = false },
        title = Txt.accountData.adminDelete,
        footer = ModalDialogConfirmActions(
            confirmText = Txt.accountData.confirmButton,
            confirmVariant = AppButtonVariant.DANGER,
            onCancel = { confirmDelete = false },
            onConfirm = {
                confirmDelete = false
                scope.launch {
                    try {
                        AdminApiClient.deleteUser(user.id)
                        ToastManager.success(Txt.accountData.adminDeleted)
                        onChanged()
                    } catch (e: ApiException) {
                        ToastManager.error(if (e.errorCode == "SUBSCRIPTION_ACTIVE") Txt.accountData.subscriptionActive else Txt.admin.saveFailed(e.serverMessage))
                    }
                }
            }
        )
    ) { SpanText(Txt.accountData.adminConfirm(user.email), Modifier.color(palette.textSecondary)) }
    var note by remember(user.id) { mutableStateOf("") }
    AppCard(Modifier.fillMaxWidth()) {
        Column(Modifier.gap(0.5.cssRem)) {
            Row(Modifier.fillMaxWidth().gap(0.5.cssRem).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
                Strong(user.email)
                val statusColor = if (user.status == "ACTIVE") palette.statusReady else palette.statusFailed
                SpanText(
                    user.status,
                    Modifier.fontSize(0.75.cssRem).fontWeight(FontWeight.SemiBold).color(statusColor)
                        .padding(topBottom = 0.15.cssRem, leftRight = 0.5.cssRem).borderRadius(999.px)
                        .backgroundColor(statusColor.toRgb().copy(alpha = 40))
                )
            }
            Muted(
                "${s.plan}: ${user.planId}${user.subscriptionStatus?.let { " ($it)" } ?: ""} · " +
                    "${s.creditsMonth}: ${user.creditsUsed}/${user.creditsLimit ?: "∞"} · ${s.purchased}: ${user.purchasedCredits} · " +
                    "${s.cost30d}: ${usd(user.costUsd30d)}"
            )
            Muted("${s.registered}: ${dateTime(user.createdAtMs)} · ${s.lastLogin}: ${dateTime(user.lastLoginAtMs)}")
            Row(Modifier.fillMaxWidth().gap(0.6.cssRem).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.Bottom) {
                Field(s.creditsDelta, delta, { delta = it.filter { c -> c.isDigit() || c == '-' } }, width = 10.0, placeholder = "+100")
                Field(s.note, note, { note = it.take(200) })
                AppButton(text = s.grant, size = AppButtonSize.SM, onClick = {
                    val credits = delta.toIntOrNull() ?: return@AppButton
                    scope.saving(onDone = { delta = ""; note = ""; onChanged() }) {
                        AdminApiClient.grantCredits(user.id, credits, note.ifBlank { null })
                    }
                })
                AppButton(text = Txt.accountData.adminDelete, icon = AppIcon.TRASH, variant = AppButtonVariant.GHOST, size = AppButtonSize.SM, onClick = { confirmDelete = true })
                AppButton(
                    text = if (user.status == "ACTIVE") s.block else s.unblock,
                    variant = if (user.status == "ACTIVE") AppButtonVariant.DANGER else AppButtonVariant.OUTLINE,
                    size = AppButtonSize.SM,
                    onClick = {
                        scope.saving(onDone = { onChanged() }) {
                            AdminApiClient.setStatus(user.id, if (user.status == "ACTIVE") "BLOCKED" else "ACTIVE")
                        }
                    }
                )
            }
        }
    }
}

@Composable
private fun ModerationTab() {
    val s = strings().admin
    val scope = rememberCoroutineScope()
    var openOnly by remember { mutableStateOf(true) }
    var incidents by remember { mutableStateOf<List<ModerationIncidentDto>>(emptyList()) }
    var version by remember { mutableStateOf(0) }
    LaunchedEffect(openOnly, version) { incidents = runCatching { AdminApiClient.incidents(openOnly) }.getOrDefault(emptyList()) }
    AppSwitch(checked = openOnly, onCheckedChange = { openOnly = it }, label = s.onlyOpen)
    if (incidents.isEmpty()) Muted(s.noIncidents)
    incidents.forEach { incident ->
        AppCard(Modifier.fillMaxWidth()) {
            Column(Modifier.gap(0.35.cssRem)) {
                Strong("#${incident.id} · ${incident.kind} · ${incident.category}")
                Muted("${dateTime(incident.createdAtMs)} · ${incident.surface}${incident.assetId?.let { " · $it" } ?: ""}${incident.sessionHash?.let { " · $it" } ?: ""}")
                Muted("${s.excerpt}: ${incident.excerpt}")
                incident.comment?.let { Muted("💬 $it") }
                if (incident.status == "OPEN") {
                    AppButton(text = s.resolve, size = AppButtonSize.SM, variant = AppButtonVariant.OUTLINE, onClick = {
                        scope.saving(onDone = { version++ }) { AdminApiClient.resolveIncident(incident.id) }
                    })
                } else {
                    Muted("✓ ${s.resolved} ${dateTime(incident.resolvedAtMs)}")
                }
            }
        }
    }
}

@Composable
private fun AuditTab() {
    val s = strings().admin
    var entries by remember { mutableStateOf<List<AdminAuditDto>>(emptyList()) }
    LaunchedEffect(Unit) { entries = runCatching { AdminApiClient.audit() }.getOrDefault(emptyList()) }
    if (entries.isEmpty()) {
        Muted(s.noData)
        return
    }
    AppCard(Modifier.fillMaxWidth()) {
        DataTable(
            headers = listOf(s.whenLabel, s.admin, s.action, s.target, s.details),
            rows = entries.map { listOf(dateTime(it.createdAtMs), it.admin, it.action, it.target, it.details ?: "—") }
        )
    }
}
